import { ref, shallowRef, computed, onBeforeUnmount } from 'vue'
import AudienceService from '@/services/AudienceService'

// Live view of an audience room for the dashboard.
//
// The dashboard joins the room over the same WebSocket viewers use, but with a
// host token, so moderators watch chat, questions and poll tallies arrive in
// real time. Moderation actions — deleting, pinning, banning, polls — go
// through the backend proxy (see AudienceService) rather than this socket, so
// ownership checks stay in one place. The socket carries exactly two outbound
// frames, both of them the host speaking as a participant: `chat.send` and
// `qa.submit`.
//
// Failure is always non-fatal: the settings half of the tab keeps working even
// if the room cannot be reached.

const MAX_MESSAGES = 300
const MAX_RECONNECT_ATTEMPTS = 6
const KEEPALIVE_MS = 25000

export const CHAT_TEXT_MAX = 500
export const QUESTION_TEXT_MAX = 300

// How long a rejection stays on screen before it stops being about anything the
// host still remembers doing.
const SEND_ERROR_TTL_MS = 8000

// An `error` frame says nothing about which frame provoked it, so it is
// attributed to whatever we sent last, within a window short enough that an
// unprompted error (a ban landing, say) is not blamed on an old message. Only
// one composer is on screen at a time, so this is accurate in practice.
const ATTRIBUTION_WINDOW_MS = 5000

/**
 * Turns a worker error code into something a host can act on. `origin` is
 * 'chat' or 'qa', which is what makes the limits and the nouns specific.
 */
function describeSendError(code, origin, serverMsg) {
	const noun = origin === 'qa' ? 'question' : 'message'

	switch (code) {
		case 'offline':
			return `Not connected to the room, so the ${noun} was not sent.`
		case 'feature_disabled':
			return origin === 'qa'
				? 'Q&A is switched off for this stream, so the question was not posted.'
				: 'Chat is switched off for this stream, so the message was not sent.'
		case 'empty':
			return `Type something first — that ${noun} was empty.`
		case 'too_long':
			return origin === 'qa'
				? `Questions are capped at ${QUESTION_TEXT_MAX} characters.`
				: `Messages are capped at ${CHAT_TEXT_MAX} characters.`
		case 'rate_limited':
			return 'You are posting faster than the room allows. Wait a moment and send it again.'
		case 'slow_mode':
			return 'Slow mode is holding the room back. Wait for the interval to pass.'
		case 'link_blocked':
			return `This room blocks links for everyone, hosts included, so the ${noun} was not posted. Take the address out and try again.`
		case 'banned':
			return 'You are banned from this room, so nothing can be posted.'
		case 'bad_frame':
		case 'unknown_type':
			return `The room could not read that ${noun}.`
		case 'internal':
			return 'The room hit an error. Try again.'
		default:
			return serverMsg || `The room turned that ${noun} down.`
	}
}

export function useAudienceRoom(streamId) {
	// 'idle' | 'connecting' | 'live' | 'reconnecting' | 'failed'
	const status = ref('idle')
	const error = ref('')
	const presence = ref(0)
	const slowModeSec = ref(0)

	const messages = ref([])
	const questions = ref([])
	const polls = ref([])
	const pinned = ref(null)
	const reactionCounts = ref({})

	// The call-to-action card every viewer currently sees, or null. Kept live
	// by `hello` and `cta.set` frames, so the dashboard's indicator moves the
	// moment a broadcast lands rather than on the next stats poll.
	const cta = ref(null)

	// Identity as the worker resolved it from our token: `{ vid, role, name,
	// feat }`. Authoritative over the settings on screen, because the token is
	// minted at join and a feature switched on since then is not in it.
	const me = ref(null)

	// The last rejection of something we tried to post:
	// `{ code, origin, message, ts }`, or null. Cleared on the next attempt.
	const sendError = ref(null)

	const socket = shallowRef(null)
	const token = ref('')
	const socketUrl = ref('')

	let attempt = 0
	let keepaliveTimer = null
	let reconnectTimer = null
	let sendErrorTimer = null
	let stopped = false
	let disposed = false

	// What we last put on the wire, so an error frame can be pointed at the
	// composer that caused it. See ATTRIBUTION_WINDOW_MS.
	let lastSend = null

	// Bumped every time we open a socket. Handlers capture the value they were
	// registered under, so a socket we have already replaced can never write to
	// shared state — see openSocket.
	let generation = 0

	// True from the moment a purge is requested until its replacement socket
	// exists. The 4410 close the purge sends us is expected during that window.
	let purgePending = false

	const isLive = computed(() => status.value === 'live')

	/** Features our own token carries, empty until `hello` lands. */
	const myFeatures = computed(() => (me.value && me.value.feat) || [])

	const canSendChat = computed(() => isLive.value && myFeatures.value.includes('chat'))
	const canSendQuestion = computed(() => isLive.value && myFeatures.value.includes('qa'))

	const openPolls = computed(() => polls.value.filter((p) => !p.closedTs))

	/** Unanswered first, then most upvoted — the order a host works through them. */
	const sortedQuestions = computed(() => {
		return [...questions.value].sort((a, b) => {
			if (!!a.answered !== !!b.answered) return a.answered ? 1 : -1
			return (b.upvotes || 0) - (a.upvotes || 0)
		})
	})

	function clearTimers() {
		if (keepaliveTimer) window.clearInterval(keepaliveTimer)
		if (reconnectTimer) window.clearTimeout(reconnectTimer)
		keepaliveTimer = null
		reconnectTimer = null
	}

	async function mintToken() {
		const res = await AudienceService.getRoomToken(streamId, 'host')
		token.value = res.token
		socketUrl.value = res.socketUrl
	}

	async function connect() {
		if (disposed) return

		stopped = false
		// A fresh budget, so the Connect button and the post-purge rejoin are
		// not starved by attempts a previous drop already spent.
		attempt = 0
		status.value = 'connecting'
		error.value = ''

		try {
			await mintToken()
		} catch (err) {
			status.value = 'failed'
			error.value = err.message || 'Could not join the room'
			purgePending = false
			return
		}

		openSocket()
	}

	function openSocket() {
		if (stopped || disposed) return

		// Each socket remembers the generation it was opened in and every
		// handler bails if it has been superseded. Without this, an event from
		// a socket we have already replaced — the 4410 close trailing a purge
		// is the one that bites — would clear the live socket's keepalive, drop
		// our reference to it and force the UI back to disconnected.
		generation += 1
		const gen = generation
		purgePending = false

		// The socket being replaced is normally already closed; closing it here
		// covers the purge case, where the server has closed its end but our
		// close event has not been delivered yet.
		clearTimers()
		if (socket.value) {
			socket.value.close()
			socket.value = null
		}

		let ws
		try {
			ws = new WebSocket(`${socketUrl.value}?token=${encodeURIComponent(token.value)}`)
		} catch (err) {
			return scheduleReconnect()
		}
		socket.value = ws

		ws.onopen = () => {
			if (gen !== generation) return
			attempt = 0
			status.value = 'live'
			keepaliveTimer = window.setInterval(() => {
				// Byte-exact: this exact string is answered by the Cloudflare
				// runtime without waking the hibernating Durable Object.
				if (ws.readyState === 1) ws.send('{"t":"ping"}')
			}, KEEPALIVE_MS)
		}

		ws.onmessage = (event) => {
			if (gen !== generation) return

			let frame
			try {
				frame = JSON.parse(event.data)
			} catch (err) {
				return
			}
			handleFrame(frame)
		}

		ws.onclose = (event) => {
			if (gen !== generation) return

			clearTimers()
			socket.value = null
			if (stopped) return

			// A purge we asked for. The rejoin is already on its way, so hold
			// the connecting state rather than flashing disconnected, and leave
			// the reconnect logic out of it whatever the close code turns out
			// to be — two sockets racing into the room helps nobody.
			if (purgePending) {
				status.value = 'connecting'
				reset()
				return
			}

			// 4410 without a purge of our own: the room is gone (deleted, or
			// cleared from somewhere else), so there is nothing to rejoin.
			if (event.code === 4410) {
				status.value = 'idle'
				reset()
				return
			}
			scheduleReconnect()
		}

		ws.onerror = () => {
			// onclose always follows; reconnection is handled there.
		}
	}

	function scheduleReconnect() {
		if (stopped) return
		attempt += 1

		if (attempt > MAX_RECONNECT_ATTEMPTS) {
			status.value = 'failed'
			error.value = 'Lost connection to the room'
			return
		}

		status.value = 'reconnecting'
		const backoff = Math.min(1000 * Math.pow(2, attempt - 1), 30000)

		reconnectTimer = window.setTimeout(async () => {
			if (stopped) return

			// One immediate retry reuses the token, since most drops are a deploy
			// or a blip. Past that, assume the hour-long token has expired — a
			// dashboard left open all day would otherwise never get back in.
			if (attempt > 1) {
				try {
					await mintToken()
				} catch (err) {
					return scheduleReconnect()
				}
			}

			openSocket()
		}, backoff + Math.random() * 500)
	}

	function clearSendError() {
		if (sendErrorTimer) window.clearTimeout(sendErrorTimer)
		sendErrorTimer = null
		sendError.value = null
	}

	function raiseSendError(code, origin, serverMsg) {
		if (sendErrorTimer) window.clearTimeout(sendErrorTimer)

		sendError.value = {
			code,
			origin: origin || null,
			message: describeSendError(code, origin, serverMsg),
			ts: Date.now()
		}

		sendErrorTimer = window.setTimeout(() => {
			sendErrorTimer = null
			sendError.value = null
		}, SEND_ERROR_TTL_MS)
	}

	/**
	 * @returns {boolean} whether the frame left the socket — not whether the
	 * room accepted it, which only ever arrives as a later `error` frame.
	 */
	function sendFrame(frame, origin) {
		clearSendError()

		const ws = socket.value
		if (!ws || ws.readyState !== 1) {
			raiseSendError('offline', origin)
			return false
		}

		try {
			ws.send(JSON.stringify(frame))
		} catch (err) {
			raiseSendError('offline', origin)
			return false
		}

		lastSend = { origin, at: Date.now() }
		return true
	}

	/**
	 * Posts to chat as the host.
	 *
	 * There is no ack. The worker echoes `chat.msg` to every connection
	 * including this one, so callers clear their input on `true` and let the
	 * echo render the message, rather than showing an optimistic copy that
	 * would then have to be reconciled or withdrawn.
	 *
	 * @param {string} text
	 * @returns {boolean}
	 */
	function sendChat(text) {
		const trimmed = (text || '').trim()

		// Checked here as well as by the worker so the obvious mistakes cost
		// nothing and read the same as a real rejection.
		if (!trimmed) {
			raiseSendError('empty', 'chat')
			return false
		}
		if (trimmed.length > CHAT_TEXT_MAX) {
			raiseSendError('too_long', 'chat')
			return false
		}

		return sendFrame({ t: 'chat.send', text: trimmed }, 'chat')
	}

	/**
	 * Submits a question as the host. Broadcasts `qa.new`; same no-ack
	 * contract as sendChat.
	 *
	 * @param {string} text
	 * @returns {boolean}
	 */
	function sendQuestion(text) {
		const trimmed = (text || '').trim()

		if (!trimmed) {
			raiseSendError('empty', 'qa')
			return false
		}
		if (trimmed.length > QUESTION_TEXT_MAX) {
			raiseSendError('too_long', 'qa')
			return false
		}

		return sendFrame({ t: 'qa.submit', text: trimmed }, 'qa')
	}

	function disconnect() {
		stopped = true
		purgePending = false
		clearSendError()
		lastSend = null
		// Retires the current socket's handlers along with it, so a close event
		// still in flight cannot report a drop after we have left.
		generation += 1
		clearTimers()
		if (socket.value) socket.value.close()
		socket.value = null
		status.value = 'idle'
	}

	/**
	 * Clears the room and rejoins it, so a moderator lands back in the empty
	 * room instead of being left disconnected by the 4410 the purge sends us.
	 *
	 * The purge response and that 4410 close race, in either order, so the
	 * rejoin is sequenced off the response — the one signal that always
	 * arrives — and the generation counter makes a late close a no-op.
	 *
	 * Throws only if the purge request itself failed, in which case the room is
	 * untouched and the socket keeps running. A rejoin that fails reports
	 * through `status` and `error` like any other connection problem, so the
	 * Connect button can retry it.
	 *
	 * @param {() => Promise<any>} purgeRequest
	 */
	async function purgeAndRejoin(purgeRequest) {
		// Armed before the request goes out, because the close can beat the
		// response back. openSocket disarms it once a replacement exists.
		purgePending = true
		const gen = generation

		try {
			await purgeRequest()
		} catch (err) {
			purgePending = false
			throw err
		}

		// Anything that took over the socket in the meantime — unmount, the
		// feature being switched off, a manual reconnect — moved the generation
		// on and owns what happens next.
		if (disposed || generation !== gen) {
			purgePending = false
			return
		}

		reset()
		await connect()
	}

	function reset() {
		messages.value = []
		questions.value = []
		polls.value = []
		pinned.value = null
		presence.value = 0
		reactionCounts.value = {}
		cta.value = null
	}

	function handleFrame(frame) {
		switch (frame.t) {
			case 'hello':
				me.value = frame.you || null
				presence.value = frame.presence || 0
				slowModeSec.value = frame.slowModeSec || 0
				pinned.value = frame.pinned || null
				messages.value = frame.recentMessages || []
				questions.value = frame.questions || []
				polls.value = frame.openPolls || []
				cta.value = frame.cta || null
				break
			case 'chat.msg':
				messages.value.push({
					id: frame.id, vid: frame.vid, name: frame.name, text: frame.text, ts: frame.ts
				})
				if (messages.value.length > MAX_MESSAGES) {
					messages.value.splice(0, messages.value.length - MAX_MESSAGES)
				}
				break
			case 'chat.del':
				messages.value = messages.value.filter((m) => m.id !== frame.id)
				if (pinned.value && pinned.value.id === frame.id) pinned.value = null
				break
			case 'chat.clear':
				// Chat only: questions, polls and votes are untouched and we stay
				// connected. The pin goes with it — the message it pointed at was
				// among those cleared, and no separate `chat.pin` follows.
				messages.value = []
				pinned.value = null
				break
			case 'chat.pin':
				pinned.value = frame.msg || null
				break
			case 'cta.set':
				// Set and removal are the same frame; removal carries null.
				cta.value = frame.cta || null
				break
			case 'qa.new':
				questions.value.push(frame.q)
				break
			case 'qa.update': {
				const q = questions.value.find((item) => item.id === frame.qid)
				if (q) {
					q.upvotes = frame.upvotes
					q.answered = frame.answered
				}
				break
			}
			case 'qa.del':
				questions.value = questions.value.filter((q) => q.id !== frame.qid)
				break
			case 'poll.open':
				polls.value = [frame.poll, ...polls.value.filter((p) => p.id !== frame.poll.id)]
				break
			case 'poll.tally': {
				const poll = polls.value.find((p) => p.id === frame.pid)
				if (poll) poll.counts = frame.counts
				break
			}
			case 'poll.close': {
				const poll = polls.value.find((p) => p.id === frame.pid)
				if (poll) {
					poll.counts = frame.counts
					poll.closedTs = Date.now()
				}
				break
			}
			case 'react.burst': {
				// Bursts are deltas for a 400ms window, so they accumulate into a
				// running total for the session rather than replacing it.
				const next = { ...reactionCounts.value }
				Object.keys(frame.counts || {}).forEach((emoji) => {
					next[emoji] = (next[emoji] || 0) + frame.counts[emoji]
				})
				reactionCounts.value = next
				break
			}
			case 'presence':
				presence.value = frame.n
				break
			case 'error': {
				// Scoped to this connection, never broadcast, and the only way
				// the room ever tells us a post was refused.
				const fresh = lastSend && Date.now() - lastSend.at < ATTRIBUTION_WINDOW_MS
				raiseSendError(frame.code, fresh ? lastSend.origin : null, frame.msg)
				break
			}
		}
	}

	onBeforeUnmount(() => {
		// Latches, unlike `stopped`, which connect() clears: nothing may reopen
		// a socket after the component is gone.
		disposed = true
		disconnect()
	})

	return {
		status,
		error,
		isLive,
		me,
		canSendChat,
		canSendQuestion,
		sendError,
		presence,
		slowModeSec,
		messages,
		questions,
		sortedQuestions,
		polls,
		openPolls,
		pinned,
		cta,
		reactionCounts,
		connect,
		disconnect,
		reset,
		purgeAndRejoin,
		sendChat,
		sendQuestion,
		clearSendError
	}
}
